"use client";

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import {
  consumePartyPassUpgradeParamsFromUrl,
  isPartyPassCelebrationSeen,
  markPartyPassCelebrationSeen,
} from "../party-pass-celebration";

export interface UsePartyPassCelebrationOptions {
  partyId: string;
  shouldCelebrate: boolean;
}

export interface UsePartyPassCelebrationResult {
  visible: boolean;
  dismiss: () => void;
  prefersReducedMotion: boolean;
}

function subscribeStorage(onStoreChange: () => void) {
  window.addEventListener("storage", onStoreChange);
  return () => window.removeEventListener("storage", onStoreChange);
}

function subscribeReducedMotion(onStoreChange: () => void) {
  if (typeof window.matchMedia !== "function") {
    return () => {};
  }

  const media = window.matchMedia("(prefers-reduced-motion: reduce)");
  media.addEventListener("change", onStoreChange);
  return () => media.removeEventListener("change", onStoreChange);
}

function getPrefersReducedMotion() {
  if (typeof window.matchMedia !== "function") {
    return true;
  }

  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function usePartyPassCelebration({
  partyId,
  shouldCelebrate,
}: UsePartyPassCelebrationOptions): UsePartyPassCelebrationResult {
  const alreadySeen = useSyncExternalStore(
    subscribeStorage,
    () => isPartyPassCelebrationSeen(window.localStorage, partyId),
    () => true,
  );
  const prefersReducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    getPrefersReducedMotion,
    () => true,
  );
  const [hidden, setHidden] = useState(false);
  const visible =
    shouldCelebrate && partyId.trim().length > 0 && !alreadySeen && !hidden;

  useEffect(() => {
    if (!visible) {
      return;
    }

    consumePartyPassUpgradeParamsFromUrl(window.location, (url) => {
      window.history.replaceState(window.history.state, "", url);
    });
  }, [visible]);

  const dismiss = useCallback(() => {
    setHidden(true);
    markPartyPassCelebrationSeen(window.localStorage, partyId);
  }, [partyId]);

  return { visible, dismiss, prefersReducedMotion };
}
